/**
 * Merge Hyperliquid's ledger with our own `wallet_transfers` rows into the
 * single "Deposit & Withdrawal" list the portfolio shows.
 *
 * Neither source alone is the whole story:
 *
 * - **HL's ledger** only knows what crossed the Hyperliquid bridge. The final
 *   leg of a real withdrawal — custodial wallet → external address — is a
 *   plain Arbitrum ERC-20 transfer that never appears in it.
 * - **`wallet_transfers`** only knows the legs *we* execute, but those are the
 *   only ones with a non-terminal state worth showing. A submit returns 200
 *   for *queued*, not *sent*.
 *
 * Before this merge a queued or failed external withdrawal was invisible in
 * the product: the submit toast was the only signal a user ever got, so a row
 * stuck in the worker looked exactly like one that had worked.
 */
import type { Transfer } from "#/lib/api/wallet";

/** What the user is looking at, which decides the row's label and colour. */
export type TransactionKind =
	/** Into the trading account (the bridge credit, or our wallet → bridge leg). */
	| "deposit"
	/** Trading account → custodial wallet. Hyperliquid's `withdraw3`. */
	| "withdraw-to-wallet"
	/** Custodial wallet → an external address. The actual cash-out. */
	| "withdraw-external";

/**
 * User-facing progress. The three in-flight values are deliberately distinct —
 * an operator asked to be able to tell a queued row from one already on chain —
 * but phrased so they describe the user's money, not our plumbing (`preparing`
 * is the treasury gas top-up; nobody needs to hear that).
 */
export type TransactionStatus =
	| "queued"
	| "preparing"
	| "confirming"
	| "completed"
	| "failed";

export type TransactionRow = {
	/** Stable React key; prefixed by source so the two can never collide. */
	id: string;
	time: number;
	hash: string | null;
	kind: TransactionKind;
	status: TransactionStatus;
	usdc: string;
	fee: string | null;
};

/** A Hyperliquid ledger entry, narrowed by the caller to deposit/withdraw. */
export type LedgerInput = {
	time: number;
	hash: string;
	type: "deposit" | "withdraw";
	usdc: string;
	fee: string | null;
};

function toStatus(status: Transfer["status"]): TransactionStatus {
	switch (status) {
		case "pending":
			return "queued";
		case "topping_up":
			return "preparing";
		case "sent":
			return "confirming";
		case "confirmed":
			return "completed";
		case "failed":
			return "failed";
	}
}

export function isTransactionInFlight(status: TransactionStatus): boolean {
	return (
		status === "queued" || status === "preparing" || status === "confirming"
	);
}

export function buildTransactionRows(
	ledger: LedgerInput[],
	transfers: Transfer[] | undefined,
): TransactionRow[] {
	const rows: TransactionRow[] = ledger.map((entry) => ({
		// The ledger is a record of what already happened, so every entry in
		// it is terminal by construction.
		id: `hl:${entry.hash}:${entry.time}`,
		time: entry.time,
		hash: entry.hash,
		kind: entry.type === "deposit" ? "deposit" : "withdraw-to-wallet",
		status: "completed",
		usdc: entry.usdc,
		fee: entry.fee,
	}));

	for (const transfer of transfers ?? []) {
		const status = toStatus(transfer.status);

		// A *confirmed* hl_deposit is already above as the bridge credit — the
		// same money, about a minute apart — so listing it again would read as
		// two separate deposits. Keep it only while it still says something the
		// ledger cannot: in flight, or failed (never credited at all).
		if (transfer.kind === "hl_deposit" && status === "completed") continue;

		const time = Date.parse(transfer.created_at);
		rows.push({
			id: `tx:${transfer.id}`,
			time: Number.isFinite(time) ? time : 0,
			hash: transfer.tx_hash,
			kind: transfer.kind === "hl_deposit" ? "deposit" : "withdraw-external",
			status,
			usdc: transfer.amount_usd,
			// Gas comes from the treasury (or the wallet dust it funded), never
			// from the user's amount — unlike Hyperliquid's flat $1 bridge fee,
			// there is genuinely nothing to show here.
			fee: null,
		});
	}

	return rows.sort((a, b) => b.time - a.time);
}
